import { Injectable } from '@angular/core';
import { Call } from '@twilio/voice-sdk';

export interface RoutedIncomingCall {
  call: Call;
  /** The call's custom parameters merged with its Twilio parameters. */
  params: Record<string, string>;
}

/** Returns true when it took the call; false lets the default incoming-call modal show it. */
export type IncomingCallHandler = (incoming: RoutedIncomingCall) => boolean;

/**
 * Lets a screen take over incoming Twilio calls of a given `referralType` instead of the
 * default incoming-call modal (app-incoming-call-notification). With no handler registered,
 * or when the handler declines, the default modal shows the call as before.
 */
@Injectable({ providedIn: 'root' })
export class IncomingCallRouterService {
  private readonly handlers = new Map<string, IncomingCallHandler>();

  /** Registers a handler; call the returned function to remove it. */
  register(referralType: string, handler: IncomingCallHandler): () => void {
    this.handlers.set(referralType, handler);
    return () => {
      if (this.handlers.get(referralType) === handler)
        this.handlers.delete(referralType);
    };
  }

  tryRoute(call: Call, params: Record<string, string>): boolean {
    const type = params['referralType'] ?? '';
    const handler = this.handlers.get(type);
    if (!handler) {
      if (type)
        console.log(
          `[IncomingCallRouter] no screen handles referralType "${type}": default modal`,
        );
      return false;
    }
    const taken = handler({ call, params });
    console.log(
      `[IncomingCallRouter] referralType "${type}" ${taken ? 'taken by its screen' : 'declined: default modal'}`,
    );
    return taken;
  }
}
